/**
 * Hud — controla a sobreposição em HTML: jogadores ONLINE (pill esquerda, com o dot
 * de conexão), MISSÕES (pill direita) e a faixa de MISSÕES CONCLUÍDAS (rodapé
 * central) — os últimos 5 jogadores a concluir missão, lado a lado, com os
 * avatares transbordando o chip (prova social p/ estimular os demais).
 *
 * Cada chip ENTRA COMEMORANDO: retrato vivo (ChipCelebrations) em que a pessoa pula
 * 3x sob confetes (as mesmas animações do avatar na cidade) e depois fica parada —
 * o canvas simplesmente PARA de ser redesenhado (o último quadro já é o retrato
 * estático). Várias celebrações correm em PARALELO: um chip
 * empurrado p/ a posição 2 segue pulando até o fim. A entrada de um chip novo
 * EMPURRA os demais com animação (FLIP), em vez de trocar de posição num salto.
 */
import { ChipCelebrations, renderAvatarPortrait } from './AvatarPortrait.js';
import { t } from '../i18n.js';

const MAX_ACHIEVERS = 5; // chips visíveis na faixa (o mais novo entra à esquerda; 5 cabem sem cortar)
// Chips montados por quadro. Montar um chip custa ~35 draw calls, 0,25 ms de CPU só
// de compose + Box3 (medido; o render e o drawImage vêm por cima), e 2 reflows
// forçados do FLIP — então os 5 sobreviventes de uma rajada de uma vez seriam 176
// draw calls num quadro só. Ver _flushInbox.
const START_BUDGET = 2;
// Teto da fila de chegada (ver addAchiever). Folgado sobre MAX_ACHIEVERS porque o
// dedup por jogador roda no flush: 64 chegadas podem ser menos de 64 jogadores.
const INBOX_MAX = 64;

export class Hud {
  constructor() {
    this.onlineEl = document.getElementById('online-value');
    this.pillOnline = document.getElementById('pill-online');
    this.missionsEl = document.getElementById('missions-value');
    this.achieversEl = document.getElementById('achievers');
    this.achieversList = document.getElementById('achievers-list');
    this.loader = document.getElementById('loader');
    this.loaderBar = document.getElementById('loader-bar-fill');
    this._celebs = null;     // ChipCelebrations (lazy — 1 renderer p/ todas)
    this._liveMap = new Map(); // chip → item da celebração em andamento
    this._inbox = [];        // conquistas do quadro, montadas em lote (ver _flushInbox)
  }

  setLoaderProgress(p) {
    if (this.loaderBar) this.loaderBar.style.width = `${Math.round(p * 100)}%`;
  }

  hideLoader() {
    this.loader?.classList.add('is-hidden');
  }

  /** Nº de jogadores na cidade; também acende o dot de conexão (âmbar → verde). */
  setOnline(count) {
    this.pillOnline?.classList.add('is-online');
    if (this.onlineEl) this.onlineEl.textContent = `${count ?? 0}`;
  }

  /** Total de missões realizadas (somatória, sem teto). Quem manda o número é o
   *  backend: as três cidades do funil dividem o mesmo contador e a Mexican Town
   *  tem o dela — ver o `score` em docs/protocol.md. */
  setMissions(done) {
    if (this.missionsEl) this.missionsEl.textContent = `${done ?? 0}`;
  }

  /** Chamado a cada frame: monta os chips que chegaram no lote deste quadro, avança
   *  TODAS as celebrações vivas e, quando cada uma termina (3 pulos + confete),
   *  congela o canvas daquele chip (ver _settleChip). */
  update(dt) {
    this._flushInbox(); // monta os chips do lote deste quadro (corte antes do trabalho)
    if (!this._celebs || !this._liveMap.size) return 0;
    const desenhados = this._celebs.update(dt) | 0;
    for (const [chip, item] of this._liveMap) {
      if (item.done) {
        this._liveMap.delete(chip);
        // terminou de verdade → congela o canvas (o último quadro é o retrato).
        // ABORTADA (contexto WebGL perdido) → o canvas não é confiável: pode ter
        // ficado em branco ou parado no meio do pulo — troca pelo visual estático.
        if (item.aborted) this._settleAborted(chip);
        else this._settleChip(chip);
      }
    }
    // quantos renders auxiliares os chips fizeram NESTE quadro — o chamador usa
    // p/ defasar o retrato do banner (evita 2+ renders aux no mesmo quadro)
    return desenhados;
  }

  /** Pré-aquece o renderer das celebrações de chip (contexto WebGL + shaders +
   *  a primeira cena/confete do pool) fora do quadro da primeira conquista. */
  prewarmAchievers(pares) {
    if (!this._celebs) this._celebs = new ChipCelebrations();
    this._celebs.prewarm(pares);
  }

  /**
   * Registra uma conquista na faixa do rodapé. NÃO monta o chip na hora: enfileira.
   *
   * O backend drena o inbox do WebSocket em LOTE por tick (ver RealBackend._tick), então
   * uma rajada de conquistas cai toda no MESMO quadro. Montar cada chip na chegada fazia
   * uma celebração completa — compose + Box3 + render + drawImage — por conquista, e só
   * DEPOIS o corte por MAX_ACHIEVERS descartava as excedentes. Medido com 200 aparências
   * reais (35,3 draw calls por retrato): uma rajada de 20 num quadro emitia 706 draw
   * calls e jogava 530 fora no mesmo quadro, sem ninguém nunca ver aqueles chips.
   *
   * Enfileirando, o corte acontece ANTES do trabalho: no flush só sobrevivem os
   * MAX_ACHIEVERS mais recentes, que são exatamente os que a faixa mostraria de todo
   * jeito. O resultado na tela é idêntico — os descartados já eram removidos antes de
   * qualquer pintura.
   */
  addAchiever(item) {
    if (!this.achieversList || !item) return;
    this._inbox.push(item);
    // Teto: o flush só roda no loop de render, e o loop PARA enquanto o contexto WebGL
    // estiver perdido (SceneManager._initContextRecovery). Sem teto a fila cresceria
    // sem limite nesse intervalo, num telão de 8 h. Só os MAX_ACHIEVERS mais recentes
    // chegariam à tela de qualquer forma; guardamos uma folga sobre isso.
    if (this._inbox.length > INBOX_MAX) this._inbox.splice(0, this._inbox.length - INBOX_MAX);
  }

  /**
   * Monta os chips que sobreviveram ao lote deste quadro, no máximo START_BUDGET por
   * quadro. O orçamento espalha o pico: 5 sobreviventes de uma vez seriam 176 draw
   * calls num quadro só; a 2 por quadro são 71.
   *
   * O que se vê numa rajada: os chips ENTRAM na faixa em levas de 2, ~1 quadro entre
   * elas — não existe chip meio-montado, o pendente simplesmente ainda não está lá.
   * Com uma conquista isolada (o caso comum) o orçamento nunca morde: ela é montada
   * no primeiro flush, um quadro depois de chegar.
   */
  _flushInbox() {
    const fila = this._inbox;
    if (!fila.length) return;
    // dedup dentro do lote: se o mesmo jogador conquistou 2x na mesma rajada, vale a
    // última (é o que a lógica de reconquista faria, chip por chip)
    // por ÍNDICE da última ocorrência, não por identidade do objeto: se o MESMO objeto
    // for enfileirado duas vezes, ambas as ocorrências satisfariam `get(id) === it` e o
    // chip seria montado e desmontado à toa.
    const ultimaDe = new Map();
    fila.forEach((it, i) => { if (it.id != null) ultimaDe.set(it.id, i); });
    const unicos = fila.filter((it, i) => it.id == null || ultimaDe.get(it.id) === i);
    // CORTE ANTES DO TRABALHO: só os MAX_ACHIEVERS mais recentes chegam a existir
    const sobreviventes = unicos.slice(-MAX_ACHIEVERS);
    fila.length = 0;
    // PERFIL FRACO (resolução em degrau baixo): no máximo UMA inicialização pesada
    // de celebração por quadro — o hook vem do main.js (estado do SceneManager)
    const orcamento = this.perfilFraco && this.perfilFraco() ? 1 : START_BUDGET;
    // os que não couberam neste orçamento voltam para a fila do próximo quadro
    const agora = sobreviventes.slice(0, orcamento);
    this._inbox.push(...sobreviventes.slice(orcamento));
    // FLIP UMA VEZ POR FLUSH: as posições são capturadas antes de QUALQUER mutação do
    // lote e a animação dispara depois de todas. Por chip, isso eram 2 reflows
    // forçados cada (getBoundingClientRect de todos + void offsetWidth) — e a captura
    // do 2º chip lia posições já em trânsito da animação do 1º, sujando o ponto de
    // partida do deslize. Agora: 1 captura + 1 animação por quadro, mesmo com 2 chips.
    const before = this._capturepositions();
    for (const it of agora) {
      // Isto roda DENTRO do loop de render (antes rodava no callback da rede).
      // Uma exceção ao montar um chip subiria pelo update() e mataria o loop inteiro —
      // a cidade congelaria por causa de um avatar de rodapé. Falha isolada por chip.
      try {
        this._mountAchiever(it);
      } catch (e) {
        console.error('falha ao montar o chip de conquista', e);
      }
    }
    this._playFlip(before);
  }

  /** Monta UM chip (o caminho que antes era o addAchiever inteiro). */
  _mountAchiever({ id, name, color, points, carAppearance, personAppearance }) {
    if (!this.achieversList) return;
    if (id != null) {
      const dup = this.achieversList.querySelector(`[data-id="${CSS.escape(String(id))}"]`);
      if (dup) {
        this._dropLive(dup); // o canvas sai junto com o chip
        dup.remove();        // reconquista → volta pra frente da fila
      }
    }
    const chip = document.createElement('span');
    chip.className = 'achiever';
    if (id != null) chip.dataset.id = String(id);
    chip._fallback = { carAppearance, personAppearance, color }; // só usado se não houver celebração viva

    let visual = null;
    if (carAppearance || personAppearance) {
      if (!this._celebs) this._celebs = new ChipCelebrations();
      const item = this._celebs.start(carAppearance, personAppearance);
      if (item) {
        item.canvas.className = 'achiever__ava';
        visual = item.canvas;
        this._liveMap.set(chip, item);
      }
    }
    if (!visual) visual = this._staticVisual(chip._fallback);

    const nm = document.createElement('span');
    nm.className = 'achiever__name';
    nm.textContent = name || t('Jogador');
    chip.append(visual, nm);
    if (points) {
      const pts = document.createElement('span');
      pts.className = 'achiever__pts';
      pts.textContent = `+${points}`;
      chip.append(pts);
    }
    this.achieversList.prepend(chip);
    while (this.achieversList.childElementCount > MAX_ACHIEVERS) {
      const out = this.achieversList.lastElementChild;
      this._dropLive(out);
      out.remove(); // os mais antigos saem pela direita
    }
    this.achieversEl?.classList.add('has-items');
    this.achieversEl?.setAttribute('aria-hidden', 'false');
    // o FLIP (empurrão animado de quem já estava) é orquestrado pelo _flushInbox:
    // uma captura antes do lote e uma animação depois — não por chip.
  }

  /** Posições atuais dos chips (p/ o FLIP do empurrão). null fora do browser. */
  _capturepositions() {
    const first = this.achieversList?.children?.[0];
    if (!first || !first.getBoundingClientRect) return null;
    const map = new Map();
    for (const ch of this.achieversList.children) map.set(ch, ch.getBoundingClientRect().left);
    return map;
  }

  /** FLIP: quem já estava na faixa desliza do lugar antigo p/ o novo (empurrado de
   *  lado pelo chip que entrou), em vez de trocar de posição num salto. */
  _playFlip(before) {
    if (!before || !before.size) return;
    const moved = [];
    for (const [ch, oldLeft] of before) {
      if (!ch.parentNode) continue; // saiu da faixa (cap/reconquista)
      const dx = oldLeft - ch.getBoundingClientRect().left;
      if (!dx) continue;
      ch.style.transition = 'none';
      ch.style.transform = `translateX(${dx}px)`;
      moved.push(ch);
    }
    if (!moved.length) return;
    void this.achieversList.offsetWidth; // força o reflow: fixa o ponto de partida
    for (const ch of moved) {
      ch.style.transition = 'transform 0.4s cubic-bezier(0.22, 0.9, 0.3, 1)';
      ch.style.transform = '';
    }
  }

  /**
   * Visual de quem NÃO conseguiu celebração viva (sem aparência, ou sem WebGL). Só
   * aqui o retrato estático é rasterizado, e sob demanda — antes ele era gerado para
   * TODA conquista no quadro em que ela chegava, mesmo quando o chip vivo o tornava
   * desnecessário.
   */
  _staticVisual({ carAppearance, personAppearance, color }) {
    // renderAvatarPortrait devolve um CANVAS 2D (cópia por drawImage, sem PNG) —
    // é um elemento pronto: entra direto no chip, sem <img> nem decodificação
    const portrait = (carAppearance || personAppearance)
      ? renderAvatarPortrait(carAppearance, personAppearance)
      : null;
    if (portrait) {
      portrait.className = 'achiever__ava';
      return portrait;
    }
    const dot = document.createElement('span');
    dot.className = 'achiever__dot';
    dot.style.background = color || 'var(--wa-green-bright)';
    return dot;
  }

  /**
   * Fim da celebração: o chip simplesmente PARA. O canvas guarda o último quadro
   * desenhado, que por construção JÁ É o retrato estático — a celebração termina com
   * a pose neutra, o enquadramento interpolado até pB (o mesmo, sem folga, do retrato
   * estático) e o confete apagado (LIFE 3,0 s < LIVE_DUR 3,05 s).
   *
   * Antes trocávamos o canvas por um <img> cujo PNG era rasterizado LÁ ATRÁS, no
   * quadro em que a conquista chegou: um render WebGL extra + toDataURL, que força
   * leitura do framebuffer da GPU e codifica o PNG de forma síncrona na thread
   * principal — o pico que fazia a tela travar ao anunciar a conquista. O pixel final
   * é o mesmo; o custo saiu inteiro.
   */
  _settleChip(chip) {
    chip.classList.add('is-settled');
  }

  /** Celebração ABORTADA (perda de contexto WebGL): o canvas do chip pode ter ficado
   *  em branco ou congelado no meio do pulo — substitui pelo visual estático (retrato
   *  rasterizado se houver contexto p/ isso; senão o dot com a cor do jogador). */
  _settleAborted(chip) {
    const visual = chip.children[0]; // o visual é sempre o 1º filho do chip
    chip.prepend(this._staticVisual(chip._fallback || {}));
    visual.remove();
  }

  /** Chip saiu da faixa: encerra a celebração dele (se houver). */
  _dropLive(chip) {
    const item = this._liveMap.get(chip);
    if (item) {
      this._celebs?.release(item);
      this._liveMap.delete(chip);
    }
  }
}
